import { Injectable, Injector, NgZone } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import {
    SetLogin,
    SetLogout,
    SetProfile,
} from '../store/actions/profile.action';
import { ActionReducerMapType } from '../store/config-reducers';
import {
    clearLocalStorage,
    clearSessionStorage,
    getLocalStorage,
    removeLocalStorage,
    setLocalStorage,
} from '../utils/storage';
import { FcmService } from './fcm.service';
import { keyStorage } from '../constants/storage';
import { NavigationExtras, Router } from '@angular/router';
import { AuthApi } from '../api/auth-api.service';
import { GenerateTokenAPI } from '../api/generate-token-api.service';
import { MenuApi } from '../api/menu-api.service';
import {
    canAccessMenu,
    menuShortcutFormatter,
    menuSidebarFormatter,
} from '../utils/utilityFunction';
import { MenuService } from './menu.service';
import { HeaderService } from './header.service';
import { ProfileService } from '../api/profile.service';
import { UserType } from '../constants/enum';
import { apiUrl } from '../constants/apiUrl';
import { HttpRequestService } from './http-request.service';

const selectReducer = (state: ActionReducerMapType) => {
    return state;
};
export const selectProfileReducer = createSelector(selectReducer, (state) => {
    return state.profileReducer;
});

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private isLoggedIn$ = new BehaviorSubject<boolean>(false);
    private redirectUrl$ = new BehaviorSubject<string>('');
    isLogin: boolean;
    privilegeMenu: string[];
    pushNotification: boolean;

    constructor(
        private store: Store<ActionReducerMapType>,
        private fcmService: FcmService,
        private zone: NgZone,
        private router: Router,
        private injector: Injector,
        private headerService: HeaderService,
        private menuService: MenuService,
        private httpService: HttpRequestService
    ) {
        this.store.pipe(select(selectProfileReducer)).subscribe((state) => {
            this.isLogin = state.isLogin;
            this.privilegeMenu = state.profileInfo.privilegeMenu;
            this.pushNotification = state.profileInfo.pushNotification;
        });
    }

    login() {
        let isReceiveNotication = getLocalStorage(
            keyStorage.isReceiveNotication
        );
        if (!isReceiveNotication) {
            setLocalStorage(keyStorage.isReceiveNotication, 'true');
            isReceiveNotication = 'true';
        }
        if (isReceiveNotication == 'true') {
            this.fcmService.subscribeTo();
            this.fcmService.addListener();
        };
        this.isLoggedIn$.next(true);
    }

    logout() {
        this.fcmService.unsubscribeFrom();
        this.isLoggedIn$.next(false);
        this.redirectUrl$.next('');
        clearLocalStorage();
        this.store.dispatch(SetLogout());
    }

    getIsLoggedIn() {
        return this.isLoggedIn$.asObservable();
    }

    getIsLogin() {
        return this.isLogin;
    }

    getPrivilegeMenu() {
        return this.privilegeMenu;
    }

    getRedirectUrl() {
        return this.redirectUrl$.asObservable();
    }

    setRedirectUrl(url: string) {
        this.redirectUrl$.next(url);
    }

    clearApplicationStorage = () => {
        clearSessionStorage();
        removeLocalStorage(keyStorage.userId);
        removeLocalStorage(keyStorage.refreshToken);
        removeLocalStorage(keyStorage.accessToken);
    };

    createParam(data: ProfileResponseType, email: string, menu: string[]) {
        const firstName = data?.firstName ?? '';
        const lastName = data?.lastName ?? '';
        const profileStorage: ProfileInfoType = {
            firstName: data?.companyName ?? firstName,
            lastName: data?.companyName ? '' : lastName,
            privilegeMenu: menu ?? [],
            pushNotification: true,
            email,
            idCard: data.idCard,
        };

        const profileStore: ProfileType = {
            firstName: data?.firstName ?? '',
            lastName: data?.lastName ?? '',
        };

        return { profileStorage, profileStore };
    }

    auth = async () => {
        const accessToken = getLocalStorage(keyStorage.accessToken);
        const authApi = this.injector.get(AuthApi);
        const menu = await authApi.getMenuByToken(accessToken);
        const profileApi = this.injector.get(ProfileService);
        const profile = await profileApi.getProfile();
        const data: ProfileResponseType = {
            token: accessToken,
            firstName: profile?.data?.firstName,
            lastName: profile?.data?.lastName,
            userType: profile?.data?.userType,
            companyName: profile?.data?.companyName,
            idCard: profile?.data?.idCard,
        };
        const { profileStorage, profileStore } = this.createParam(
            data,
            profile.data?.userId,
            menu
        );

        // this.platform.ready().then(() => {
        this.fcmService.initialization();
        // });

        setLocalStorage(keyStorage.isLogin, 'true');
        setLocalStorage(keyStorage.menuKey, menu.join());

        this.store.dispatch(SetLogin({ data: profileStorage }));
        this.store.dispatch(SetProfile({ profileInfo: profileStore }));
        this.login();
        await this.initialMenu();
        // this.getPDPA(); //waiting
        this.goToPage();
    };

    goToPage() {
        // this.getNotiBadge(); //waiting
        this.headerService.setDisplayHeader(true);
        this.getRedirectUrl().subscribe((url: string) => {
            let redirectUrl = url;

            if (
                !redirectUrl ||
                !canAccessMenu(this.getPrivilegeMenu(), redirectUrl)
            ) {
                redirectUrl = '/';
            }
            this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
        });
    }

    async initialMenu() {
        const menuApi = this.injector.get(MenuApi);

        const menuSidebar: MenuResponseType = await menuApi.getMenu();
        this.menuService.setMenuSidebar(menuSidebarFormatter(menuSidebar));

        const menuShortcut = await menuApi.getMenuShortcut();
        this.menuService.setMenuShortcut(menuShortcutFormatter(menuShortcut));
    }

    processVerifyToken = async (portalToken: string) => {
        const generateToken = this.injector.get(GenerateTokenAPI);
        const tokenInfo = await generateToken.getToken(portalToken);

        if (tokenInfo.data?.refreshToken && tokenInfo.data?.accessToken) {
            setLocalStorage(keyStorage.isAdmin, '1');
            setLocalStorage(
                keyStorage.refreshToken,
                tokenInfo.data?.refreshToken
            );
            setLocalStorage(
                keyStorage.accessToken,
                tokenInfo.data?.accessToken
            );

            this.auth();
        } else {
            this.clearApplicationStorage();
        }
    };

    goToLoginPage = () => {
        this.zone.run(() => {
            this.clearApplicationStorage();
            this.navigateLogin({ replaceUrl: true });
        });
    };

    goToMainPage = () => {
        this.zone.run(() => {
            this.clearApplicationStorage();
            this.router.navigate([''], { replaceUrl: true });
        });
    };

    navigateLogin = (extras?: NavigationExtras) => {
        this.router.navigate(['/auth/login'], extras);
    };

    automateRedirectPage = () => {
        const refreshUrl = window.location.pathname;
        const isNotLandingUrl =
            window.location.pathname.indexOf('/landing') === -1;
        if (isNotLandingUrl) {
            // this.goToPage();
        } else {
            this.goToPageDefault();
        }
    };

    goToPageDefault = () => {
        if (this.privilegeMenu != null && this.privilegeMenu.length > 0) {
            //go to page : follow url
        } else {
            this.goToMainPage();
        }
    };

    authWithToken = async () => {
        let isAuth = false;
        let refreshToken: string = getLocalStorage(keyStorage.refreshToken);
        await this.initialMenu();

        if (refreshToken) {
            //how to check ?? ดำน้ำก่อน รอประกอบให้เหมือนกันกับประชาชน ที่พี่ฟลอยจะปรับ service ใหม่ //waiting
            const accessToken = getLocalStorage(keyStorage.accessToken);
            const authApi = this.injector.get(AuthApi);
            const profileApi = this.injector.get(ProfileService);
            const profile = await profileApi.getProfile();
            const menu = await authApi.getMenuByToken(accessToken);

            if (menu != null) {
                const data: ProfileResponseType = {
                    token: accessToken,
                    firstName: profile?.data?.firstName,
                    lastName: profile?.data?.lastName,
                    userType: profile?.data?.userType,
                    companyName: profile?.data?.companyName,
                    idCard: profile?.data?.idCard,
                };
                const { profileStorage, profileStore } = this.createParam(
                    data,
                    profile.data?.userId,
                    menu
                );

                setLocalStorage(keyStorage.isLogin, 'true');

                if (data?.userType?.id === UserType.CITIZEN) {
                    setLocalStorage(
                        keyStorage.fullName,
                        `${profile.data?.firstName ?? ''} ${
                            profile.data?.lastName ?? ''
                        }`
                    );
                    setLocalStorage(keyStorage.isAdmin, '0');
                } else if (data?.userType?.id === UserType.COMPANY) {
                    setLocalStorage(
                        keyStorage.fullName,
                        `${profile.data?.companyName ?? ''}`
                    );
                    setLocalStorage(keyStorage.isAdmin, '0');
                } else {
                    /** ADMIN LOGIN HERE */
                    await this.renewToken(refreshToken);
                    setLocalStorage(keyStorage.isAdmin, '1');
                    setLocalStorage(
                        keyStorage.fullName,
                        `${profile.data?.firstName ?? ''} ${
                            profile.data?.lastName ?? ''
                        }`
                    );
                }

                setLocalStorage(keyStorage.menuKey, menu.join());

                this.store.dispatch(SetLogin({ data: profileStorage }));
                this.store.dispatch(SetProfile({ profileInfo: profileStore }));
                this.login();
                isAuth = true;
            }
        }

        return isAuth;
    };

    async renewToken(refreshToken: string) {
        await this.httpService
            .axiosGet(`${apiUrl.getToken}?refreshToken=${refreshToken}`)
            .then((response) => {
                setLocalStorage(
                    keyStorage.accessToken,
                    response.data.accessToken
                );
                setLocalStorage(
                    keyStorage.refreshToken,
                    response.data.refreshToken
                );
            });
    }
}
